import React, { useState } from 'react';
import Divider from '@mui/material/Divider';
import './IDOnboardingView.less';
import NavigationBar from '../../../../components/NavigationBar/NavigationBar';
import { GA_TITLES, isDashboardScreen, isStaging } from '../../../../../utils';
import BrandButton from '../../../../components/BrandButton/BrandButton';
import BrandIcon from '../../../../components/BrandIcon/BrandIcon';
import { isLocalizeAdded, LocalizePropType } from 'getaway-data-layer';
import { FormikValues } from 'formik';
import ToggleTab from '../../../../components/ToggleTab/ToggleTab';
import Markdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import CheckboxListItem from '../../../../components/CheckboxListItem/CheckboxListItem';
import { InputLabel, MenuItem, Select } from '@mui/material';

type Props = {
  localize: LocalizePropType;
  onClose: () => void;
  formik: FormikValues;
  scanEnabled: boolean;
  onClickDisabled: () => void;
  isLoading: boolean;
  showResubmissionUI: boolean;
  resubmissionInstruction: string;
  passportSupportedCountries: string[];
  identitySupportedCountries: string[];
};

const IDOnboardingView = ({
  localize,
  onClose,
  formik,
  scanEnabled,
  onClickDisabled,
  isLoading,
  showResubmissionUI,
  resubmissionInstruction,
  passportSupportedCountries,
  identitySupportedCountries,
}: Props) => {
  const isIDCountriesListEnabled = () =>
    process.env.VITE_ENABLE_ID_COUNTRIES_LIST === 'true' && isStaging();

  const steps = ['stepOne', 'stepTwo', 'stepThree', 'stepFour', 'stepFive'];
  const [IDSelectedItems, setIDSelectedItems] = useState<boolean[]>(() =>
    steps.map(() => false)
  );

  const [IDHighlightedItems, setIDHighlightedItems] = useState<boolean[]>(() =>
    steps.map(() => false)
  );

  const [IDRejectionReasonItem, setIDRejectionReasonItem] =
    useState<boolean>(false);

  const [
    IDRejectionReasonItemHighlighted,
    setIDRejectionReasonItemHighlighted,
  ] = useState<boolean>(false);

  const [IDCountry, setIDCountry] = useState(
    identitySupportedCountries?.includes('Deutschland')
      ? 'Deutschland'
      : identitySupportedCountries[0]
  );
  const [passportCountry, setPassportCountry] = useState(
    passportSupportedCountries?.includes('Deutschland')
      ? 'Deutschland'
      : passportSupportedCountries[0]
  );

  const generatedStepsCount = steps.filter((step, index) => {
    const localizedText = localize(
      `ID.${formik.values?.documentType?.toLowerCase()}.${step}`
    );
    if (localizedText.includes('.step') || localizedText.trim().length === 0) {
      return false;
    }
    return true;
  })?.length;

  const checkIDItemsSelected =
    showResubmissionUI &&
    IDSelectedItems.slice(0, generatedStepsCount).every(
      (item) => item === true
    );

  const checkRejectionItemSelected =
    showResubmissionUI && IDRejectionReasonItem;

  const handleClick = () => {
    if (!showResubmissionUI) {
      formik.handleSubmit();
    }

    if (showResubmissionUI) {
      checkIDItemsSelected && checkRejectionItemSelected
        ? formik.handleSubmit()
        : !checkRejectionItemSelected &&
          setIDRejectionReasonItemHighlighted(true);

      !checkIDItemsSelected &&
        IDSelectedItems.map((step, index) =>
          step === false
            ? setIDHighlightedItems((prev) => {
                const newState = [...prev];
                newState[index] = true;
                return newState;
              })
            : false
        );
    }
  };

  const supportedCountries =
    formik.values?.documentType === 'ID_CARD'
      ? identitySupportedCountries
      : passportSupportedCountries;

  return (
    <div className="screen-container">
      <NavigationBar
        title={localize('ID.title')}
        description={localize('ID.desr')}
        onClose={onClose}
        showHelp
        showArrowBack={isDashboardScreen()}
      />
      <div
        className={
          isLocalizeAdded(localize, 'ID.faq')
            ? 'screen-body-faq'
            : 'screen-body'
        }
      >
        <div
          className={
            isLocalizeAdded(localize, 'ID.faq')
              ? 'screen-body-content-faq'
              : 'screen-body-content'
          }
        >
          <ToggleTab
            localize={localize}
            optionOne={localize(`id.tab.card`)}
            optionTwo={localize(`id.tab.passport`)}
            isOptionOneSelected={formik.values?.documentType === 'ID_CARD'}
            onToggleValueChange={() => {
              if (formik.values?.documentType === 'ID_CARD') {
                formik.setFieldValue('documentType', 'PASSPORT');
              } else {
                formik.setFieldValue('documentType', 'ID_CARD');
              }

              setIDSelectedItems((prev) => {
                return prev.map(() => false);
              });

              setIDRejectionReasonItem(false);

              setIDRejectionReasonItemHighlighted(false);
              setIDHighlightedItems((prev) => {
                return prev.map(() => false);
              });
              setIDCountry(
                identitySupportedCountries?.includes('Deutschland')
                  ? 'Deutschland'
                  : identitySupportedCountries[0]
              );
              setPassportCountry(
                passportSupportedCountries?.includes('Deutschland')
                  ? 'Deutschland'
                  : passportSupportedCountries[0]
              );
            }}
          />
          <div className="add-ID-img-container">
            <img
              alt="ID"
              src={
                formik.values?.documentType === 'ID_CARD'
                  ? '/images/ID-de.png'
                  : '/images/passport.png'
              }
              className="add-ID-img"
            />
          </div>
          {isIDCountriesListEnabled() && supportedCountries?.length > 0 && (
            <div>
              <InputLabel id="country-label">
                {localize('DL.issuecountry.placeholder')}
              </InputLabel>
              <Select
                fullWidth
                required
                labelId="select-country"
                id="country"
                variant="standard"
                label={localize('DL.issuecountry.placeholder')}
                value={
                  formik.values?.documentType === 'ID_CARD'
                    ? IDCountry
                    : passportCountry
                }
                onChange={(event: any) => {
                  formik.values?.documentType === 'ID_CARD'
                    ? setIDCountry(event.target.value)
                    : setPassportCountry(event.target.value);
                }}
                style={{ marginBottom: 16 }}
              >
                {supportedCountries?.map((item) => (
                  <MenuItem key={item} value={item}>
                    {localize(`${item}`)}
                  </MenuItem>
                ))}
              </Select>
            </div>
          )}
          {showResubmissionUI &&
            resubmissionInstruction &&
            !(passportCountry === 'other' || IDCountry === 'other') && (
              <CheckboxListItem
                selected={IDRejectionReasonItem}
                title={localize('id.rejected.title')}
                onPress={() => {
                  setIDRejectionReasonItem((prev) => !prev);
                  setIDRejectionReasonItemHighlighted(false);
                }}
                higlighted={IDRejectionReasonItemHighlighted}
                BottomComponent={
                  <div
                    data-testid="AccountTabView-readonlyDialog"
                    className="account-ID-warning"
                    onClick={() => {
                      setIDRejectionReasonItem((prev) => !prev);
                      setIDRejectionReasonItemHighlighted(false);
                    }}
                  >
                    <div className="account-warning-msg">
                      {resubmissionInstruction}
                    </div>
                  </div>
                }
              />
            )}
          {!(passportCountry === 'other' || IDCountry === 'other') &&
            steps.map((step, index) => {
              const localizedText = localize(
                `ID.${formik.values?.documentType?.toLowerCase()}.${step}`
              );
              if (
                localizedText.includes('.step') ||
                localizedText.trim().length === 0
              ) {
                return null;
              }
              return (
                <>
                  {showResubmissionUI ? (
                    <CheckboxListItem
                      key={index}
                      selected={IDSelectedItems[index]}
                      title={localizedText}
                      onPress={() => {
                        setIDSelectedItems((prev) => {
                          const newState = [...prev];
                          newState[index] = !prev[index];
                          return newState;
                        });
                        setIDHighlightedItems((prev) => {
                          const newState = [...prev];
                          newState[index] = false;
                          return newState;
                        });
                      }}
                      higlighted={IDHighlightedItems[index]}
                    />
                  ) : (
                    <>
                      <div key={index} className="add-ID-item-container">
                        <BrandIcon
                          name="selected"
                          style={{ width: 24, height: 24, marginRight: 18 }}
                        />

                        <div className="add-ID-item-text">{localizedText}</div>
                        {index < steps.length - 1 && <Divider />}
                      </div>
                      <Divider />
                    </>
                  )}
                </>
              );
            })}

          {(IDCountry === 'other' || passportCountry === 'other') && (
            <div className="add-DL-warning">
              {formik.values?.documentType === 'ID_CARD'
                ? localize('id.card.country.not.valid')
                : localize('passport.country.not.valid')}
            </div>
          )}

          <BrandButton
            id={GA_TITLES.ON_BOARDING.ADD_ID}
            title={localize('ID.button.text')}
            onClick={handleClick}
            disabled={
              isLoading ||
              !scanEnabled ||
              !formik.getFieldProps('documentType')?.value ||
              IDCountry === 'other' ||
              passportCountry === 'other'
            }
            enableKeyboardEffect={false}
            formik={formik}
            onClickDisabled={scanEnabled ? () => {} : onClickDisabled}
            isLoading={isLoading}
          />
        </div>
        {isLocalizeAdded(localize, 'ID.faq') ? (
          <div className="faq">
            <Markdown rehypePlugins={[rehypeRaw]}>
              {localize('ID.faq')}
            </Markdown>
          </div>
        ) : (
          <>
            <div className="screen-vertical-space" />
            <div className="screen-vertical-space" />
          </>
        )}
        <div className="screen-vertical-space" />
        <div className="screen-vertical-space" />
      </div>
    </div>
  );
};

export default IDOnboardingView;
